import { useState, useEffect } from "react";
import { AdvertisementDetail, AdvertisementDto } from "../interface/device.details";
import {
  buildAdSequence,
  getNoFlightsAdMode,
  getActiveFooterAds,
  getActiveRatioAds,
} from "../utils/ads-utils";

interface UseAdSequenceReturn {
  currentAd: AdvertisementDto | null;
  currentIndex: number;
  adSequence: any[];
  isRecurring: boolean;
}

export const useAdSequence = (advertisement?: AdvertisementDetail): UseAdSequenceReturn => {
  const [adSequence, setAdSequence] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);

  useEffect(() => {
    if (!advertisement) {
      setAdSequence([]);
      setCurrentIndex(0);
      setIsRecurring(false);
      return;
    }

    const noFlightsMode = getNoFlightsAdMode(advertisement);

    // Scenario: no flights + ratio active => default UI (no ads)
    if (noFlightsMode === "ratio") {
      setAdSequence([]);
      setCurrentIndex(0);
      setIsRecurring(false);
      return;
    }

    const flightsExist = !!advertisement.flightDuration && advertisement.flightDuration > 0;

    // persistentRatio is TOP-LEVEL (only activate if ratio ads are active now)
    const persistentRatio =
      !!(advertisement as any).persistentRatio && getActiveRatioAds(advertisement).length > 0;

    // Flights exist + footer ads active now => show footer ads continuously during time range
    const persistentFooter = flightsExist && getActiveFooterAds(advertisement).length > 0;

    const _advertisement = { ...advertisement };
    const infiniteLoop = _advertisement.ads?.some((x) => x.repeat === -1) ? true : false;
    setIsRecurring(infiniteLoop);

    // Normalize repeat=-1 to a finite repeat count (same as your original behavior)
    if (infiniteLoop) {
      const maxRepeatValue = _advertisement.ads.reduce((max, current) => {
        return current.repeat > max.repeat ? current : max;
      });

      _advertisement.ads = _advertisement.ads.map((x) => {
        if (x.repeat === -1) {
          return {
            ...x,
            repeat: maxRepeatValue.repeat !== -1 ? maxRepeatValue.repeat : 1,
          };
        }
        return x;
      });
    }

    let seq = buildAdSequence(_advertisement, {
      noFlightsMode,
      persistentRatio,
      persistentFooter,
    });

    // If build returns empty => no ad (currentAd=null)
    if (!seq || seq.length === 0) {
      setAdSequence([]);
      setCurrentIndex(0);
      setIsRecurring(false);
      return;
    }

    // Only apply the old "parking/gap" logic when NOT in persistent modes
    // (persistent modes are ads-only and should loop)
    const inPersistentMode = persistentFooter || persistentRatio || noFlightsMode === "fullscreen";

    if (!inPersistentMode) {
      if (!infiniteLoop && seq.length > 0) {
        seq[seq.length - 1].duration = Infinity;
      } else {
        if (
          seq.length > 1 &&
          seq[seq.length - 1].type === "" &&
          seq[seq.length - 2].type === ""
        ) {
          seq = seq.filter((x) => x.duration);
        } else if (seq.length > 0 && seq[seq.length - 1].type === "") {
          seq[seq.length - 1].duration = advertisement.flightDuration;
        }
      }

      // If flightDuration is 0/undefined => remove gaps so we don't fall back to flights UI
      if (!advertisement.flightDuration || advertisement.flightDuration === 0) {
        seq = seq.filter((x) => x.type);
      }
    }

    setAdSequence(seq);
    setCurrentIndex(0);
  }, [advertisement]);

  useEffect(() => {
    if (!advertisement) return;
    if (!adSequence.length) return;

    const current = adSequence[currentIndex];

    // Safety: if current is missing, reset
    if (!current) {
      setCurrentIndex(0);
      return;
    }

    // Infinity means "park" (only used in non-persistent modes)
    if (current.duration === Infinity) return;

    // If duration is null/undefined => jump forward (don’t get stuck)
    if (current.duration == null) {
      const nextIndex = currentIndex + 1 < adSequence.length ? currentIndex + 1 : 0;
      setCurrentIndex(nextIndex);
      return;
    }

    const timer = setTimeout(() => {
      const nextIndex = currentIndex + 1 < adSequence.length ? currentIndex + 1 : 0;

      // On wrap, rebuild sequence so time-window changes are applied
      if (nextIndex === 0) {
        const noFlightsMode = getNoFlightsAdMode(advertisement);

        if (noFlightsMode === "ratio") {
          setAdSequence([]);
          setCurrentIndex(0);
          setIsRecurring(false);
          return;
        }

        const flightsExist = !!advertisement.flightDuration && advertisement.flightDuration > 0;
        const persistentRatio =
          !!(advertisement as any).persistentRatio && getActiveRatioAds(advertisement).length > 0;
        const persistentFooter = flightsExist && getActiveFooterAds(advertisement).length > 0;

        const _advertisement = { ...advertisement };

        const infiniteLoop = _advertisement.ads?.some((x) => x.repeat === -1) ? true : false;
        if (infiniteLoop) {
          const maxRepeatValue = _advertisement.ads.reduce((max, current) => {
            return current.repeat > max.repeat ? current : max;
          });

          _advertisement.ads = _advertisement.ads.map((x) => {
            if (x.repeat === -1) {
              return {
                ...x,
                repeat: maxRepeatValue.repeat !== -1 ? maxRepeatValue.repeat : 1,
              };
            }
            return x;
          });
        }

        let seq = buildAdSequence(_advertisement, {
          noFlightsMode,
          persistentRatio,
          persistentFooter,
        });

        if (!seq || seq.length === 0) {
          setAdSequence([]);
          setCurrentIndex(0);
          setIsRecurring(false);
          return;
        }

        // Only apply old end/gap fixes if NOT persistent
        const inPersistentMode = persistentFooter || persistentRatio || noFlightsMode === "fullscreen";
        if (!inPersistentMode) {
          if (
            seq.length > 1 &&
            seq[seq.length - 1].type === "" &&
            seq[seq.length - 2].type === ""
          ) {
            seq.splice(seq.length - 1);
          } else if (
            advertisement.flightDuration &&
            seq.length > 0 &&
            seq[seq.length - 1].type === "" &&
            !seq[seq.length - 1].duration
          ) {
            seq[seq.length - 1].duration = advertisement.flightDuration;
          }

          if (!advertisement.flightDuration || advertisement.flightDuration === 0) {
            seq = seq.filter((x) => x.type);
          }
        }

        setAdSequence(seq);
        setIsRecurring(infiniteLoop);
        setCurrentIndex(0);
        return;
      }

      setCurrentIndex(nextIndex);
    }, Number(current.duration) * 1000);

    return () => clearTimeout(timer);
  }, [currentIndex, adSequence, advertisement]);

  return {
    currentAd: adSequence[currentIndex] ?? null,
    currentIndex,
    adSequence,
    isRecurring,
  };
};
